import { useStore } from '@nanostores/react';
import { accountStore, authStore } from './store';
import type { AugmentedAccount } from './types';

export const AccountFeatureFlag = {
  DeveloperToolsPanel: 'developer-tools-panel',
  WebPush: 'web-push',
  Whatsapp: 'whatsapp',
  StoreAnonEvents: 'store_anon_events',
  JavascriptWebhook: 'javascript_webhook',
  LegacyCustomSmtpBilling: 'legacy_custom_smtp_billing',
  MultiLanguageSettings: 'multi_language_settings',
  ExitOnlyAfterMessage: 'exit_only_after_message',
  ParcelAmp: 'parcel_amp',
  CdpDataCatalogEnabled: 'cdp_data_catalog_enabled',
  VirtualSupportChatEnabled: 'virtual_support_chat_enabled',
  ParcelInHydra: 'parcel_in_hydra',
  InAppLegacyEditor: 'in_app_legacy_editor',
  FlagCDPUIConsolidation: 'cdp_ui_consolidation',

  ShowGoals: 'show-goals',
  GoalsMilestones: 'goals-milestones',
  GoalsOnboardingRecommendations: 'goals-onboarding-recommendations',
  GoalsEmbed: 'goals-embed',
  GoalsToConversions: 'goals-to-conversions',
  OutcomeOptimization: 'outcome_optimization',
  McpExternalAccountToggle: 'mcp-external-account-toggle',
  TasksPerformanceDashboard: 'tasks_performance_dashboard_enabled',
  SegmentAutoArchive: 'segment_auto_archive',
  // Pulled from Ember feature flags
  ActivityLogsTimeRange: 'activity-logs-time-range',
  BranchingAnyObjectCondition: 'branching_any_object_condition',
  StateBasedObjectCampaignTrigger: 'state-based-object-campaign-trigger',
  GoalsCreatePreview: 'goals-create-preview',
  AiAutoSegmentNames: 'ai_auto_segment_names',
  DeliverabilityAnalytics: 'deliverability-analytics',
  TransactionalSms: 'transactional-sms',
  SmsByoForNative: 'sms-byo-for-native',
  ContentFirstInBroadcast: 'content-first-in-broadcast',
  NewsletterRateLimitWithTimezone: 'newsletter-rate-limit-timezone',
  NewsletterQueuedSend: 'newsletter-queued-send',
  NewsletterSendDeadline: 'newsletter_send_deadline',
  NewsletterRecipientsUpload: 'newsletter-recipients-upload',
  StoForNewsletters: 'sto_for_newsletters',
  NewsletterRedisSendTiming: 'newsletter-redis-send-timing',
  InAppEditorLeadForm: 'in-app-editor-lead-form',
  DefaultFromIdentity: 'default-from-identity',
  EmailAutoTls: 'email-auto-tls',
  ConfiguredWebhooks: 'configured_webhooks',
  LLMActionBYOK: 'llm-action-byok',
  LLMActionGuided: 'llm-action-guided',
  InAppForms: 'in-app-forms',
  SignUpForms: 'sign-up-forms',
  TeamMembersExport: 'team_member_export',
  ObjectWaitUntil: 'obj_wait_until',
  JourneyAttributes: 'journey-attributes',
  WhatsappNativeFreeText: 'whatsapp-native-free-text',
  ShowTemplatesSnippetsLayouts: 'show_templates_snippets_layouts',
  InAppGlobalStyles: 'in-app_global-styles',
  HalloweenStickies2025: 'halloween_stickies_2025',
  OptimizedMessageLibrary: 'optimized-message-library',

  DsSocialIcons: 'ds-social-icons',
  DsCountdown: 'ds-countdown',
  DsVideo: 'ds-video',
  DsUiPlayground: 'ds-ui-playground',
  ImproveDsAgent: 'improve-ds-agent',
  AgentFirstEmails: 'agent-first-emails',
  ClassicEditorsSunset: 'classic-editors-sunset',

  InAppTooltips: 'in-app-tooltips',
  InAppLivePreview: 'in-app_live-preview',
  InAppDesignStudio: 'in-app_design-studio',
  DsInfiniteCanvas: 'ds-infinite-canvas',
  DsUseTiptap: 'ds-use-tiptap',
  PausePlanM2: 'pause-plan-m2',
  PavedRoad: 'paved_road',

  MultipleShortlinkDomains: 'multiple_shortlink_domains',
  NotificationsUx: 'notifications-ux',
  HomeUx: 'home-ux',
  ServiceAccounts: 'service-accounts',
  TestDeliveryMode: 'test_delivery_mode',
  TemplateEngineSwitch: 'template_engine_switch',
  AccountVerification: 'account-verification',
  PushV2: 'push_v2',
  MultiApp: 'multi_app',
  DsLiquidExpressions: 'ds-liquid-expressions',
  Gravatar: 'gravatar',
  GravatarEnabled: 'gravatar_enabled',
  AllowAgentEditLiveData: 'allow_agent_edit_live_data',
  AiEndpointLocked: 'ai_endpoint_locked',
  McpAllowEditLiveData: 'mcp_allow_edit_live_data',
  McpAllowAccessSensitiveData: 'mcp_allow_access_sensitive_data',
  AgentOpusModel: 'agent-opus-model',
  AgentFloatingLauncher: 'agent_floating_launcher',
  CdpSalesforceEcaMigration: 'cdp_salesforce_eca_migration',
  CdpSalesforceEcaMigrationOptout: 'cdp_salesforce_eca_migration_optout',
  CdpTrackEnabled: 'cdp_track_enabled',
  CdpPassthroughEnabled: 'cdp_passthrough_enabled',
  CdpDatabricksSourceEnabled: 'databricks_source_enabled',
  WhiteLabelTwilio: 'white_label_twilio',
  WhiteLabelTwilioOnboarding: 'white_label_twilio_onboarding',
  FrequencyCaps: 'frequency_caps',
  CampaignDraftMode: 'campaign-draft-mode',
  EditorBreadcrumbs: 'editor-breadcrumbs',
  DsInsertMenuPreviews: 'ds-insert-menu-previews',
  DsRecommendedProfiles: 'ds-recommended-profiles',
  DsReviewAndTestSeparateButtons: 'ds-review-and-test-separate-buttons',
  PhoneIdentifier: 'phone-identifier',
  OpenTrackingConsent: 'open_tracking_consent',
  ProfileGrowth: 'profile_growth',
  NewPipelinesObjectsPricing: 'new_pipelines_objects_pricing',
  NewPipelinesObjectsBilling: 'new_pipelines_objects_billing',
  BuilderOfferEnabled: 'builder_offer_enabled',
  SlackAgentEnabled: 'slack_agent_enabled',
  GenericSamlSso: 'generic_saml_sso',
  OrdwayCouponCodes: 'ordway_coupon_codes',
  LegacyTwilio: 'legacy-twilio',
  DryadConditionPicker: 'dryad-condition-picker',
  InAppToursAction: 'in-app_tours_action',
  EssentialsOnboarding: 'essentials_onboarding',
  OnboardingExperimentTest: 'onboarding_experiment_test',
  CdpJourneysSqlImportAvailable: 'cdp_journeys_sql_import_available',
  ReverseEtlConsolidation: 'reverse_etl_consolidation',
  ConsentRecords: 'consent_records',
  DashboardEditorPoc: 'dashboard_editor_poc',
  WorkflowBuilderMigration: 'workflow-builder-migration',
  ComposerMigration: 'composer-migration',
  ObjectsMigration: 'objects-migration',
  ProfilePageMigration: 'profile-page-migration',
  SmsSettingsMigration: 'sms-settings-migration',
  BroadcastsMigration: 'broadcasts-migration',
  OneTimeSendOverviewMigration: 'one-time-send-overview-migration',
  OneTimeSendSetupMigration: 'one-time-send-setup-migration',
  PauseResumeNewsletters: 'pause_resume_newsletters',
  AudiencesIntegrationsMigration: 'audiences-integrations-migration',
  FormsIntegrationsMigration: 'forms-integrations-migration',
  BillingUI2026: 'billing-ui-2026',
  ExperimentAction: 'experiment-action',
  QueryObjectsAction: 'query-objects-action',
  PolygonGeofences: 'polygon-geofences',
  GeofenceDwell: 'geofence-dwell',
  HealthDelaySplitPanels: 'three-panel-delay-chart',
  PrefixedApiKeys: 'prefixed-api-keys',
  ObjectCampaignBackfill: 'object_campaign_backfill',
  ContentPreviewOptimization: 'content_preview_optimization',
} as const;

export type AccountFeatureFlag = (typeof AccountFeatureFlag)[keyof typeof AccountFeatureFlag];

export type FeatureFlag = AccountFeatureFlag;
export const FeatureFlag = { ...AccountFeatureFlag };

export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = Object.freeze({
  [AccountFeatureFlag.DeveloperToolsPanel]: false,
  [AccountFeatureFlag.WebPush]: false,
  outcome_optimization: false, // internal prototype: agent-driven goal A/B testing
  whatsapp: false, // whether to enable sending to whatsapp numbers in Twilio SMS action
  store_anon_events: true, // enables the collection of anonymous events.
  javascript_webhook: false, // enables adding javascript reporting webhook
  legacy_custom_smtp_billing: true, // whether to skip message overages for accounts with custom smtp https://github.com/customerio/issues/5947
  multi_language_settings: false, // enables custom multi-language settings
  exit_only_after_message: false, // enables the "exit only after receiving a message" exit option for campaigns
  parcel_amp: true, // enables AMP in Parcel
  cdp_data_catalog_enabled: false, // enabled Data Catalog menu item and page in CDP
  virtual_support_chat_enabled: false, // enables the virtual support chat via Zendesk widget
  parcel_in_hydra: false, // enables Parcel in Hydra
  in_app_legacy_editor: false, // enables the legacy in-app editor
  cdp_ui_consolidation: false, //if enabled, CDP is now presented inside the "Integrations" and no sidebar is displayed

  'show-goals': false, // will gate the goals surfaces (route, sidebar link, command bar, automations/broadcasts filters, home prompts); consumers land in follow-ups (FLO-4772). Keep the default false: the account payload carries only enabled flags, so an absent key has to read as off
  'goals-milestones': false, // enables the goals milestones feature
  'goals-onboarding-recommendations': false, // enables the recommendations-based goals empty state, built from the workspace's own converting conditions (FLO-4432)
  'goals-embed': false, // enables the goal-attachments-editor section (attach an existing goal) on automations, FLO-4339
  'goals-to-conversions': false, // enables the Conversions tab on the data index, which reads the workspace's goals as conversions (AUD-4578)
  'mcp-external-account-toggle': false, // mcp external account toggle
  tasks_performance_dashboard_enabled: false, // enables the tasks performance dashboard
  segment_auto_archive: false, // enables auto-archive behavior for unused segments
  // Ember feature flags
  'activity-logs-time-range': false, // enables the activity logs time range feature
  branching_any_object_condition: true, // on for every account by default; offers the attribute-source select on a condition reading the current person's relationship to an object
  'state-based-object-campaign-trigger': false, // lets the account save the consolidated Object (rel_seg_attr) campaign trigger; the trigger picker offers it only with this on
  'goals-create-preview': false, // enables the preview-first goal create page in place of the stepper wizard
  ai_auto_segment_names: false, // enables the AI auto segment names feature
  'deliverability-analytics': false, // enables the deliverability analytics feature
  'transactional-sms': false, // enables the transactional SMS feature
  'sms-byo-for-native': false, // lets a Customer.io-managed SMS workspace connect its own providers too
  'content-first-in-broadcast': false, // enables content and channel selection before recipients in broadcasts
  'newsletter-rate-limit-timezone': false, // enables the newsletter rate limit timezone feature
  'newsletter-queued-send': false, // holds a newsletter send while a referenced segment is still building, instead of rejecting it at the API boundary (FLO-4596)
  newsletter_send_deadline: false,
  'newsletter-recipients-upload': false,
  sto_for_newsletters: false, // offers Send time optimization on an email one-time send's Schedule step
  'newsletter-redis-send-timing': false, // swaps the Newsletter Reports hourly chart for the Send volume chart
  'in-app-editor-lead-form': false, // enables the in-app editor lead form feature
  'default-from-identity': false, // enables the default from identity feature
  'email-auto-tls': false, // enables the email auto TLS feature
  configured_webhooks: false, // enables the configured webhooks feature
  'llm-action-byok': false, // enables the LLM action BYOK feature
  'llm-action-guided': false, // enables generating an LLM action from a description
  'in-app-forms': false, // enables the in-app forms feature
  'sign-up-forms': false, // enables the sign-up forms page
  team_member_export: false, // enables the team member export feature
  obj_wait_until: false, // enables the obj wait until feature
  'journey-attributes': false, // enables the journey attributes feature
  'whatsapp-native-free-text': false, // enables the WhatsApp native free text feature
  show_templates_snippets_layouts: false, // enables the show templates snippets layouts feature
  'in-app_global-styles': false, // enables the in-app global styles feature
  halloween_stickies_2025: false, // enables the Halloween stickies 2025 feature
  'optimized-message-library': false, // enables the optimized message library feature
  'ds-social-icons': false, // enables the social icons component feature
  'ds-countdown': false, // enables the countdown component feature
  'ds-video': false, // enables the video component feature
  'ds-ui-playground': false, // enables the design studio UI playground
  'improve-ds-agent': false, // consolidated Design Studio agent-improvements flag: the brand design system document (its Brand section on the Global Styles page), distilled org patterns, and the inline AI edit quality tier
  'agent-first-emails': false, // agent-first email creation: the DS create-email starting-point flow (CON-8087); the ui repo gates its automation, broadcast and transactional starters on the same key
  'classic-editors-sunset': false,

  'in-app-tooltips': false, // enables the in-app tooltips feature
  'in-app_live-preview': false, // enables the in-app live preview feature
  'in-app_design-studio': false, // rollout flag for authoring in-app messages as Design Studio nodes
  'ds-infinite-canvas': false, // enables the infinite canvas visual editor in Design Studio
  'ds-use-tiptap': false, // enables the tiptap-based visual editor in Design Studio
  'pause-plan-m2': false, // enables the real implementation of account pausing
  paved_road: false, // enables the CDP paved road onboarding experience
  multiple_shortlink_domains: false, // enables the multiple shortlink domains feature
  'notifications-ux': false, // enables the new notifications drawer UX
  'home-ux': false, // enables the home UX feature
  'service-accounts': false, // enables the service accounts UI on the API credentials page
  test_delivery_mode: true,
  template_engine_switch: false, // lets a workspace switch its default template engine (liquid version) even without can_update_default_template_engine
  'account-verification': false,
  push_v2: false, // Delivery-per-device push sending
  multi_app: false, // enables multiple mobile apps for push
  'ds-liquid-expressions': false, // enables liquid conditional logic in Design Studio
  gravatar: false, // enables the gravatar feature
  gravatar_enabled: false, // whether external avatars (e.g. Gravatar) are fetched for the account
  allow_agent_edit_live_data: false, // default off — admins opt in from Privacy & AI to let AI agents edit live data
  ai_endpoint_locked: false, // support-set in Flightdeck: freezes the EU/Global AI endpoint so only support can change it
  mcp_allow_edit_live_data: false, // off by default; self-serve signups get it turned on at account creation
  mcp_allow_access_sensitive_data: false, // off by default — admins opt in from AI & MCP settings
  'agent-opus-model': false, // lets the account choose which model the agent runs on
  agent_floating_launcher: false, // replaces the "Ask agent" footer with the agent footer bar and its conversation chips

  cdp_salesforce_eca_migration: false,
  cdp_salesforce_eca_migration_optout: false, // when on, treats cdp_salesforce_eca_migration as off (lets accounts opt out of the ECA migration)
  cdp_track_enabled: false,
  cdp_passthrough_enabled: false, // routes Journeys Track-API data through Pipelines sources (with cdp_track_enabled, gates auto-track sources)
  databricks_source_enabled: false, // gates the Databricks source: connection form, auth-error banner and per-row reconnect state
  white_label_twilio: false, // white-labelled Twilio integration
  white_label_twilio_onboarding: false, // SMS registration onboarding; when off, the SMS settings page redirects to the Twilio settings page
  frequency_caps: false,
  'campaign-draft-mode': false, // enables draft mode for automations: editing a live campaign as a draft that publishes atomically
  'editor-breadcrumbs': false, // enables Hydra breadcrumbs on editor/composer pages
  'ds-insert-menu-previews': false, // enables insert menu previews in Design Studio
  'ds-recommended-profiles': false, // shows recommended profiles + highlights liquid syntax inline in Design Studio Preview Mode
  'ds-review-and-test-separate-buttons': false, // splits the Design Studio review and test actions into separate buttons
  'phone-identifier': false, // enables using a phone number as a profile identifier
  open_tracking_consent: false, // enables the "only track opens from people who have consented" workspace setting
  profile_growth: false, // enables the Profile growth tab on the Plans & Billing page
  new_pipelines_objects_pricing: false, // enables the "new pipelines objects pricing" plan highlights on the Your Plan page
  new_pipelines_objects_billing: false, // combines person + object profile overages into one line on billing-history invoices
  builder_offer_enabled: false, // offers a Builder downgrade in the cancel flow and on the account-canceled page. Builder is paused (SPE-4075), so the offer stays hidden until Flightdeck turns this on.
  slack_agent_enabled: false, // enables the customer-installable Slack agent
  generic_saml_sso: false, // enables the Generic SAML 2.0 identity provider option on the SSO settings page
  ordway_coupon_codes: false, // enables the Ordway coupon/draft-subscription flow on the checkout page
  'legacy-twilio': false, // enables the original Twilio integration
  'dryad-condition-picker': false, // replaces the people filter's add-condition and row-type selects with the faceted condition picker; same key as Ember's flag
  'in-app_tours_action': false, // offers the Product Tour tile in the workflow builder's Add panel
  essentials_onboarding: false, // Essentials-trial guided onboarding takeover; the cohort is trial + non-builder plan + this flag (see features/onboarding/completion.ts)
  onboarding_experiment_test: false, // throwaway A/B test of the experiment exposure setup: recolors a test button on the wizard's first question
  cdp_journeys_sql_import_available: false, // shows SQL Import integrations in the directory even when no import is set up
  consent_records: false, // enables Consent Record Management feature
  reverse_etl_consolidation: false, // the SQL-sync pages forward to Pipelines Reverse ETL, which has replaced them
  dashboard_editor_poc: false, // proof-of-concept Boards pages (/workspaces/:id/boards)
  'workflow-builder-migration': false, // rollout flag: serves the automation workflow builder (canvas) from Hydra instead of the embedded Ember app (FPS-2338)
  'composer-migration': false, // rollout flag: serves the action composer for migrated action types from Hydra instead of the embedded Ember app (FPS-4673)
  'objects-migration': false, // serves the custom objects list from Hydra instead of the embedded Ember app (FPS-2707 rollout flag)
  'profile-page-migration': false, // rollout flag: serves the person profile from Hydra instead of the embedded Ember app, one tab at a time as each tab ships. Today: the header, tab bar and Attributes tab (FPS-4208). Keep it on dogfood accounts until the last tab lands, since each tab goes live on merge for flagged accounts
  'one-time-send-overview-migration': false,
  'one-time-send-setup-migration': false, // rollout flag: serves the one-time send setup wizard (/broadcasts/newsletter/:id/setup, every step) from Hydra instead of the embedded Ember app (FPS-2325)
  'broadcasts-migration': false, // rollout flag: serves the Broadcasts list (/broadcasts, both tabs) from Hydra instead of the embedded Ember app (FPS-2322)
  pause_resume_newsletters: false, // Ember's one-time send pause and resume; here it only renames a stopped send "Canceled" on the Broadcasts list
  'audiences-integrations-migration': false, // rollout flag: serves the ad-audience setup page from Hydra instead of the embedded app (FPS-2796)
  'forms-integrations-migration': false, // rollout flag: serves the Forms integration pages from Hydra instead of the embedded app (FPS-2841)
  'sms-settings-migration': false, // rollout flag: serves the SMS and Twilio channel settings from Hydra instead of the embedded Ember app (FPS-2350, FPS-2840)
  'billing-ui-2026': false, // new billing UI supporting the Flex plan
  'experiment-action': false, // gates the Experimentation hub and the in-automation Experiment action
  'query-objects-action': false, // gates the Query Objects workflow step: its add-actions tile and its failure's Activity Type filter option. The backend refuses to create the step without it
  'polygon-geofences': false, // enables polygon geofence management after the backend and mobile contracts are ready
  'geofence-dwell': false,
  'three-panel-delay-chart': false, // splits workspace health delay chart into three separate panels
  'prefixed-api-keys': false, // creates public wk_ keys for client-app sources and shows ak_/wk_ keys (MBL-2550, MBL-2470)
  object_campaign_backfill: false, // offers the backfill choice when an object or relationship automation starts or is scheduled. An account flag set in services, not FlightDeck
  content_preview_optimization: false, // cuts a workflow email's Content preview to its first 30,000 characters so a large email does not stall the panel (FLO-2072)
});

const useFeatureFlags = () => {
  const account = useStore(accountStore);
  const flags = account?.feature_flags ?? {};

  return Object.assign({}, FEATURE_FLAG_DEFAULTS, flags);
};

export const useFeatureFlag = (flag: FeatureFlag | string) => {
  const flags = useFeatureFlags();
  return flags[flag] ?? false;
};

/**
 * Pure flag check for an explicit account. When the account is null (not
 * loaded yet), the flag's registered default applies — callers that render
 * before auth completes must treat that answer as provisional and re-evaluate
 * once the account arrives (subscribe to `accountStore`, or use a reactive
 * hook like `useFeatureFlag`).
 */
export const accountHasFeatureFlag = (
  account: Pick<AugmentedAccount, 'feature_flags'> | null | undefined,
  flag: FeatureFlag,
) => {
  const defaultValue = FEATURE_FLAG_DEFAULTS[flag] ?? false;

  if (account == null) {
    return defaultValue;
  }

  return account.feature_flags?.[flag] ?? defaultValue;
};

/**
 * Call-time snapshot of `accountHasFeatureFlag` against the current auth
 * store. Safe in event handlers and other post-auth code paths; do NOT use
 * during render for anything that gets cached — the pre-auth answer is the
 * flag default and won't recompute (see `useFeatureFlag`).
 */
export const hasFeatureFlag = (flag: FeatureFlag) => {
  return accountHasFeatureFlag(authStore.get()?.account, flag);
};
